import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  TextField,
  Select,
  IconButton,
} from "@mui/material";
import { Visibility } from "@mui/icons-material"; // Eye icon
import { fetchNotifications, fetchNotificationCount } from "../../redux/slices/myAccount/notificationsSlice";
import { useLanguage } from "../../context/LanguageContext/LanguageContext";
import NotFound from "../NotFoundService/NotFoundService";
import CustomPagination from "./Pagination/Pagination";
import { useNavigate } from "react-router-dom";
import DateFilterDropdown from "./DateFilterDropdown";
import axios from "axios";
import axiosInstance from "../../api/axiosInstance";

const Notifications = () => {
  const { isLoggedIn, token } = useSelector((state) => state.login);
  const navigate = useNavigate();
  const { translations, language, direction } = useLanguage();
  const t = translations;
  const dispatch = useDispatch();
  const { items: notifications, status, error, count ,totalFilteredRecords } = useSelector(
    (state) => state.notifications
  );
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notificationDateFrom, setnotificationDateFrom] = useState("");
  const [notificationDateTo, setnotificationDateTo] = useState("");
  const [isRead, setIsRead] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/signin");
      return;
    }

    const filters = {
      draw: page + 1,
      start: page * rowsPerPage,
      length: rowsPerPage,
      isweb:true
    };

    if (startDate) filters.notificationDateFrom = startDate;
    if (endDate) filters.notificationDateTo = endDate;
    if (isRead !== "") filters.isRead = isRead;
    dispatch(fetchNotificationCount({ isRead: filters.isRead, token, language }));
    dispatch(fetchNotifications({ filters, token, language }));
  }, [dispatch, page, rowsPerPage, token, language, startDate, endDate, isRead]);

  // Handle marking as read
  const handleMarkAsRead = async (notification) => {
    try {
      await axiosInstance.get(`/api/Common/markNotificationAsRead?id=${notification.userNotificationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update local state to reflect the read status
      dispatch(fetchNotifications({ filters: { isRead, page }, token, language }));

      // Navigate to order details page
      navigate(`/my-account/orders/${notification.objectId}`);
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  if (!isLoggedIn) return null;
  
  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 1 }}>
        {t.notifications}
      </Typography>
      <Typography sx={{ color: "#12538B", fontWeight: 500, marginBottom: 3 }}>
        {status === "loading" ? t.notificationsloading : t.unreadnotifications}
      </Typography>
  
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          marginBottom: 3,
          justifyContent: { md: "end", xs: "center" },
          flexDirection: { md: "row", xs: "column" },
        }}
      >
        <DateFilterDropdown
          t={t}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          direction={direction}
        />
  
        <Select
          value={isRead}
          onChange={(e) => setIsRead(e.target.value)}
          displayEmpty
          size="small"
          sx={{
            width: 200,
            borderRadius: "12px",
            color: "var(--text-color)", // Text color
            borderColor: "var(--primary-color)", // Border color
            backgroundColor: "var(--secondary-color)", // Background color
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    borderColor: "var(--primary-color)", // Border color for the input field
                },
                "&:hover fieldset": {
                    borderColor: "var(--primary-color)", // Border color on hover
                },
                "&.Mui-focused fieldset": {
                    borderColor: "var(--primary-color)", // Border color when focused
                },
            },
            direction
        }}
        >
          <MenuItem value=""  sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.all}</MenuItem>
          <MenuItem value={true} sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.read}</MenuItem>
          <MenuItem value={false} sx={{ justifyContent: direction === "ltr" ? "flex-end" : "flex-start"}}>{t.unread}</MenuItem>
        </Select>
      </Box>
  
      {/* Notification Cards */}
      {notifications.data && notifications.data.length > 0 ? (
        notifications.data.map((notification, index) => (
          <Card
            key={notification.userNotificationId || index}
            sx={{ marginBottom: 2, boxShadow: 2, borderRadius: 2,     
            backgroundColor: notification.isRead ? "transparent" : "rgba(0, 123, 255, 0.1)", // Highlight unread notifications
            }}
          >
            <CardContent
              onClick={() => handleMarkAsRead(notification)}
              sx={{ cursor: "pointer" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {notification.notificationTitle_Localized}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ marginTop: 1 }}
                  >
                    {notification.notificationDetailsMessage_Localized}
                  </Typography>
                </Box>
  
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMarkAsRead(notification);
                  }}
                  sx={{ justifyContent: "end" }}
                >
                  <Visibility sx={{ color: "var(--primary-color)" }} />
                </IconButton>
              </Box>
  
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  marginTop: 2,
                  textAlign: direction === "ltr" ? "right" : "left",
                }}
              >
                {new Date(notification.notificationDate).toLocaleDateString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  }
                )}
              </Typography>
            </CardContent>
          </Card>
        ))
      ) : (
        // Fallback message for no notifications
        <Typography
          sx={{
            textAlign: "center",
            color: "text.secondary",
            marginTop: 5,
            fontWeight: "500",
          }}
        >
          {t.noNotifications}
        </Typography>
      )}
  
      {/* Pagination */}
      {notifications.data && notifications.data.length > 0 && (
        <CustomPagination
          page={page}
          setPage={setPage}
          totalData={count}
          itemsPerPage={rowsPerPage}
        />
      )}
    </Box>
  );
  
};

export default Notifications;
